import Tabs from '@/js/tabs.js';

// Matches the `--large-up` custom media breakpoint used in the stylesheet.
const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
const MAX_VISIBLE_CARDS = 4;
const SCROLL_EPSILON = 2;

class CollectionSeriesView extends Tabs {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onFilterFormChange = this.onFilterFormChange.bind(this);
    this.onDesktopMediaChange = this.onDesktopMediaChange.bind(this);
    this.onElementResize = this.onElementResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  setup() {
    this.desktopMediaQuery = globalThis.matchMedia(DESKTOP_MEDIA_QUERY);
    this.addEventListeners();
    this.syncRowsToViewport();
    this.syncPlaceholdersAndScrollButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListeners();
  }

  addEventListeners() {
    this.removeEventListeners();
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onFilterFormChange);
    this.addEventListener('toggle', this.onToggle, true);
    this.addEventListener('scroll', this.onScroll, {
      capture: true,
      passive: true
    });
    this.desktopMediaQuery.addEventListener(
      'change',
      this.onDesktopMediaChange
    );

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(this.onElementResize);
    }

    this.resizeObserver.observe(this);
  }

  removeEventListeners() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onFilterFormChange);
    this.removeEventListener('toggle', this.onToggle, true);
    this.removeEventListener('scroll', this.onScroll, true);
    this.desktopMediaQuery?.removeEventListener(
      'change',
      this.onDesktopMediaChange
    );
    this.resizeObserver?.disconnect();
  }

  /**
   * @param {Event} event
   */
  handleTabButtonClick(event) {
    super.handleTabButtonClick(event);
    this.onTabButtonClick(event.currentTarget);
  }

  /**
   * @param {EventTarget | null} button
   */
  onTabButtonClick(button) {
    if (!(button instanceof HTMLElement)) return;

    const view = button.dataset.tabIndex;
    this.dataset.plpView = view;

    for (const tabButton of this.tabButtonElems) {
      const isSelected = tabButton === button;
      tabButton.setAttribute('aria-selected', String(isSelected));

      if (isSelected) {
        tabButton.setAttribute('aria-current', 'true');
      } else {
        tabButton.removeAttribute('aria-current');
      }
    }

    if (view === 'series') {
      requestAnimationFrame(() => {
        this.syncPlaceholdersAndScrollButtons();
      });
    }
  }

  /**
   * @param {Event} event
   */
  onClick(event) {
    const target =
      event.target instanceof Element
        ? event.target
        : event.target.parentElement;
    if (!target) return;

    const summary = target.closest('[data-series-row-summary]');
    if (summary && this.desktopMediaQuery.matches) {
      event.preventDefault();
    }

    const scrollButton = target.closest(
      '[data-series-scroll-next], [data-series-scroll-prev]'
    );
    if (scrollButton instanceof HTMLElement) {
      this.scrollTrackForButton(scrollButton);
    }

    if (target.closest('[data-remove-single-filter]')) {
      this.activateView('all-beer');
    }
  }

  /**
   * Filtering is only meaningful on the All Beer product grid.
   * @param {Event} event
   */
  onFilterFormChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.closest('[data-filter-form]')) return;

    this.activateView('all-beer');
  }

  /**
   * @param {string} view
   */
  activateView(view) {
    const button = this.querySelector(`[data-tab-index="${view}"]`);
    if (!(button instanceof HTMLElement) || button.hasAttribute('active')) {
      return;
    }

    this.handleTabButtonClick({
      currentTarget: button,
      target: button
    });
  }

  /**
   * @param {Event} event
   */
  onToggle(event) {
    const row = event.target;
    if (!(row instanceof HTMLDetailsElement)) return;
    if (!Object.hasOwn(row.dataset, 'seriesRow')) return;

    this.updateToggleLabels(row);

    if (row.open) {
      const track = row.querySelector('[data-series-products-track]');
      const previousButton = row.querySelector('[data-series-scroll-prev]');
      const nextButton = row.querySelector('[data-series-scroll-next]');
      if (track instanceof HTMLElement) {
        this.syncPlaceholdersForTrack(track);
        this.syncScrollButtons(track, previousButton, nextButton);
      }
    }

    if (this.desktopMediaQuery.matches || !row.open) return;

    for (const otherRow of this.querySelectorAll('[data-series-row]')) {
      if (otherRow !== row) {
        otherRow.open = false;
      }
    }
  }

  /**
   * @param {Event} event
   */
  onScroll(event) {
    const track = event.target;
    if (!(track instanceof HTMLElement)) return;
    if (!Object.hasOwn(track.dataset, 'seriesProductsTrack')) return;

    const row = track.closest('[data-series-row]');
    const previousButton = row?.querySelector('[data-series-scroll-prev]');
    const nextButton = row?.querySelector('[data-series-scroll-next]');
    this.syncScrollButtons(track, previousButton, nextButton);
  }

  onDesktopMediaChange() {
    this.syncRowsToViewport();
  }

  onElementResize() {
    this.syncPlaceholdersAndScrollButtons();
  }

  /**
   * @param {HTMLDetailsElement} row
   */
  updateToggleLabels(row) {
    const openLabel = row.querySelector('[data-series-open-label]');
    const closeLabel = row.querySelector('[data-series-close-label]');

    if (!(openLabel instanceof HTMLElement)) return;
    if (!(closeLabel instanceof HTMLElement)) return;

    openLabel.hidden = row.open;
    closeLabel.hidden = !row.open;
  }

  syncRowsToViewport() {
    const isDesktop = this.desktopMediaQuery.matches;

    for (const row of this.querySelectorAll('[data-series-row]')) {
      if (!(row instanceof HTMLDetailsElement)) continue;

      row.open = isDesktop;
      this.updateToggleLabels(row);

      const summary = row.querySelector('[data-series-row-summary]');
      if (!(summary instanceof HTMLElement)) continue;

      if (isDesktop) {
        summary.setAttribute('tabindex', '-1');
      } else {
        summary.removeAttribute('tabindex');
      }
    }
  }

  syncPlaceholdersAndScrollButtons() {
    const seriesPanel = this.querySelector('[data-tab-panel="series"]');
    if (!seriesPanel?.hasAttribute('active')) return;

    for (const row of this.querySelectorAll('[data-series-row]')) {
      const track = row.querySelector('[data-series-products-track]');
      const previousButton = row.querySelector('[data-series-scroll-prev]');
      const nextButton = row.querySelector('[data-series-scroll-next]');

      if (!(track instanceof HTMLElement)) continue;

      this.syncPlaceholdersForTrack(track);
      this.syncScrollButtons(track, previousButton, nextButton);
    }
  }

  /**
   * @param {HTMLElement} track
   */
  syncPlaceholdersForTrack(track) {
    for (const existing of track.querySelectorAll(
      '[data-series-placeholder]'
    )) {
      existing.remove();
    }

    const productCards = track.querySelectorAll('[data-series-product-card]');
    const visibleSlots = this.getVisibleSlotCount(track);
    const missingSlots = Math.max(0, visibleSlots - productCards.length);

    if (missingSlots === 0) return;

    const placeholderMarkup = this.createPlaceholderElement();

    for (let index = 0; index < missingSlots; index += 1) {
      track.append(placeholderMarkup.cloneNode(true));
    }
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  getVisibleSlotCount(track) {
    const firstCard = track.querySelector('[data-series-product-card]');
    if (!(firstCard instanceof HTMLElement)) return MAX_VISIBLE_CARDS;

    const trackStyles = globalThis.getComputedStyle(track);
    const gap =
      Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    const cardWidth = firstCard.getBoundingClientRect().width;

    if (cardWidth <= 0) return MAX_VISIBLE_CARDS;

    const availableWidth = track.clientWidth;
    const slots = Math.floor((availableWidth + gap) / (cardWidth + gap));

    return Math.min(MAX_VISIBLE_CARDS, Math.max(1, slots));
  }

  /**
   * @returns {HTMLLIElement}
   */
  createPlaceholderElement() {
    const item = this.ownerDocument.createElement('li');
    item.className = 'collection-series-placeholder';
    item.dataset.seriesPlaceholder = '';
    item.setAttribute('aria-hidden', 'true');

    const image = this.ownerDocument.createElement('img');
    image.className = 'placeholder-image';
    image.loading = 'lazy';
    image.width = 324;
    image.height = 525;
    image.alt = '';
    image.src = this.getPlaceholderImageUrl();

    item.append(image);
    return item;
  }

  /**
   * @returns {string}
   */
  getPlaceholderImageUrl() {
    if (this.placeholderImageUrl) return this.placeholderImageUrl;

    this.placeholderImageUrl = this.dataset.placeholderImageUrl || '';
    return this.placeholderImageUrl;
  }

  /**
   * @param {HTMLElement} button
   */
  scrollTrackForButton(button) {
    const row = button.closest('[data-series-row]');
    const track = row?.querySelector('[data-series-products-track]');
    if (!(track instanceof HTMLElement)) return;

    const direction = Object.hasOwn(button.dataset, 'seriesScrollPrev')
      ? -1
      : 1;

    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: 'smooth'
    });
  }

  /**
   * @param {HTMLElement} track
   * @param {Element | null | undefined} previousButton
   * @param {Element | null | undefined} nextButton
   */
  syncScrollButtons(track, previousButton, nextButton) {
    const isDesktop = this.desktopMediaQuery.matches;
    const productCards = track.querySelectorAll('[data-series-product-card]');
    const visibleSlots = this.getVisibleSlotCount(track);
    const canOverflow = productCards.length > visibleSlots;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const canScrollPrevious =
      isDesktop && canOverflow && track.scrollLeft > SCROLL_EPSILON;
    const canScrollNext =
      isDesktop &&
      canOverflow &&
      track.scrollLeft < maxScrollLeft - SCROLL_EPSILON;

    if (previousButton instanceof HTMLElement) {
      previousButton.hidden = !canScrollPrevious;
    }

    if (nextButton instanceof HTMLElement) {
      nextButton.hidden = !canScrollNext;
    }
  }
}

customElements.get('collection-series-view') ||
  customElements.define('collection-series-view', CollectionSeriesView);

export default CollectionSeriesView;
